import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Play, Pause, CheckCircle, RotateCcw, Clock, Trophy } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import api from "../lib/api.js";

const COLORS = {
  walking: "#2fbf71",
  posture: "#00B4D8",
  breathing: "#a78bfa",
  mindfulness: "#a78bfa",
  recovery: "#F59E0B",
  stretching: "#84cc16",
  corporate: "#94A3B8",
};

const TIPS = {
  walking: [
    "Eyes ahead, chin parallel to the ground.",
    "Lift your chest gently — imagine a string at the crown of your head.",
    "Let your arms swing naturally from the shoulders.",
    "Land softly on your heel and roll through to your toes.",
  ],
  posture: [
    "Align your ears over your shoulders.",
    "Engage your lower abs without holding your breath.",
    "Relax your shoulders down and back.",
    "Stack your ribcage over your pelvis.",
  ],
  breathing: [
    "Inhale through the nose, 4 counts.",
    "Hold for 2.",
    "Exhale through the mouth, 6 counts.",
    "Feel your spine lengthen with each breath.",
  ],
  mindfulness: [
    "Notice your body without judgment.",
    "Scan from crown to feet.",
    "Soften any tension as you breathe.",
    "Return to neutral spine.",
  ],
  recovery: [
    "Roll slowly — never on bone.",
    "Pause on tender spots for 20-30s.",
    "Breathe deeply to release tension.",
    "Hydrate after rolling.",
  ],
  default: [
    "Stay present and gentle.",
    "Listen to your body.",
    "Quality over speed.",
    "Posture is the goal.",
  ],
};

export default function LessonPlayer() {
  const { lessonId } = useParams();
  const nav = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [elapsed, setElapsed] = useState(0); // seconds
  const [playing, setPlaying] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(0);
  const intRef = useRef(null);

  useEffect(() => {
    api.get(`/content/${lessonId}`).then((r) => {
      setLesson(r.data);
      if (r.data.completed) setCompleted(true);
    }).catch(() => { toast.error("Lesson not found"); nav("/library"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  useEffect(() => {
    if (!lesson) return;
    const total = (lesson.duration_min || 5) * 60;
    if (playing) {
      intRef.current = setInterval(() => {
        setElapsed((e) => {
          if (e + 1 >= total) {
            clearInterval(intRef.current);
            setPlaying(false);
            handleComplete();
            return total;
          }
          return e + 1;
        });
      }, 1000);
    } else {
      clearInterval(intRef.current);
    }
    return () => clearInterval(intRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, lesson]);

  const handleComplete = async () => {
    try {
      const r = await api.post(`/content/${lessonId}/complete`);
      setCompleted(true);
      setPointsEarned(r.data.points_earned || 0);
      if (r.data.points_earned) toast.success(`Lesson complete! +${r.data.points_earned} pts`);
      else toast("Already completed earlier ✓");
    } catch (e) { toast.error("Couldn't save completion"); }
  };

  const reset = () => { setElapsed(0); setPlaying(false); };

  if (!lesson) return <PhoneFrame><div className="p-6 text-slate-400">Loading…</div></PhoneFrame>;

  const total = (lesson.duration_min || 5) * 60;
  const pct = Math.min(100, (elapsed / total) * 100);
  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  const tm = String(Math.floor(total / 60)).padStart(2, "0");
  const ts = String(total % 60).padStart(2, "0");
  const color = COLORS[lesson.category] || "#2fbf71";
  const tips = TIPS[lesson.category] || TIPS.default;
  const tipIdx = Math.min(tips.length - 1, Math.floor((elapsed / total) * tips.length));

  return (
    <PhoneFrame>
      <div className="px-5 pt-6 pb-10 min-h-screen flex flex-col">
        <button onClick={() => nav("/library")} className="text-slate-400 inline-flex items-center gap-1 text-sm" data-testid="lesson-back">
          <ChevronLeft size={16} /> Library
        </button>

        <div className="mt-5 text-center">
          <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color }}>
            {lesson.category}
          </div>
          <h1 className="ps-heading text-2xl font-bold mt-1" data-testid="lesson-title">{lesson.title}</h1>
          <div className="mt-2 inline-flex items-center gap-3 text-xs text-slate-400">
            <span className="inline-flex items-center gap-1"><Clock size={12} /> {lesson.duration_min} min</span>
            <span className="inline-flex items-center gap-1"><Trophy size={12} color="#F59E0B" /> {lesson.points_reward} pts</span>
          </div>
        </div>

        <div className="mt-7 mx-auto relative" style={{ width: 240, height: 240 }}>
          <svg width="240" height="240" className="-rotate-90">
            <circle cx="120" cy="120" r="108" stroke="#142534" strokeWidth="14" fill="none" />
            <circle
              cx="120" cy="120" r="108" stroke={color} strokeWidth="14" fill="none"
              strokeDasharray={2 * Math.PI * 108}
              strokeDashoffset={2 * Math.PI * 108 * (1 - pct / 100)}
              strokeLinecap="round"
              style={{ transition: "stroke-dashoffset 1s linear" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-5xl font-bold ps-heading" data-testid="lesson-timer">{mm}:{ss}</div>
            <div className="text-xs text-slate-400 mt-1">of {tm}:{ts}</div>
            {completed && <CheckCircle size={22} color="#2fbf71" className="mt-2" />}
          </div>
        </div>

        <div className="mt-7 flex justify-center gap-3">
          <button
            data-testid="lesson-reset"
            onClick={reset}
            className="w-14 h-14 rounded-full bg-[#142534] text-slate-300 flex items-center justify-center"
            aria-label="Reset"
          ><RotateCcw size={20} /></button>
          {!completed ? (
            <button
              data-testid="lesson-playpause"
              onClick={() => setPlaying((p) => !p)}
              className="w-16 h-16 rounded-full bg-[#2fbf71] text-[#0a1822] flex items-center justify-center shadow-lg shadow-[#2fbf71]/30"
              aria-label={playing ? "Pause" : "Play"}
            >
              {playing ? <Pause size={28} /> : <Play size={28} fill="#0a1822" />}
            </button>
          ) : (
            <button
              data-testid="lesson-finish"
              onClick={() => nav("/library")}
              className="ps-button-primary px-6 inline-flex items-center gap-2"
            >
              <CheckCircle size={18} /> Done
            </button>
          )}
          <button
            data-testid="lesson-skip"
            onClick={handleComplete}
            disabled={completed}
            className="w-14 h-14 rounded-full bg-[#142534] text-slate-300 flex items-center justify-center disabled:opacity-40 text-xs font-bold"
            aria-label="Mark complete"
          >SKIP</button>
        </div>

        <div className="mt-8 ps-card p-5">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-2">Coach cue</div>
          <div className="ps-heading text-lg font-semibold leading-snug" data-testid="lesson-tip">
            {playing || completed ? tips[tipIdx] : "Take a slow breath. Tap play when ready."}
          </div>
        </div>

        {completed && pointsEarned > 0 && (
          <div className="mt-4 ps-card p-4 flex items-center gap-3 border-[#2fbf71]/40">
            <CheckCircle size={20} color="#2fbf71" />
            <div className="flex-1 text-sm font-semibold">Lesson complete</div>
            <div className="text-[#2fbf71] font-bold">+{pointsEarned} pts</div>
          </div>
        )}
      </div>
    </PhoneFrame>
  );
}
